import { useState, ChangeEvent, useEffect } from 'react'
import { filter } from 'ramda'

import ControlsBar from 'app/common/ui-next/controls-bar'
import SaleTypesData from 'app/dataProviders/SaleTypesData'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'
import PlainButtonFilterControl from 'app/common/ui-next/plain-button-filter-control'
import { PageTemplateFilter } from 'app/common/templates-next/page-template'
import { DishId } from 'app/common/types/id'
import DishesData from 'app/dataProviders/dishes/DishesData'
import { useCheckboxActions } from 'app/common/templates-next/table-template'

import Add, { DishesAddButton } from './Add'
import DishesGear from '../SettingsGear/DishesGear'
import { DishesSearch } from './DishesSearch'
import ExportButton from './ExportButton'
import DishesActionsButton from './DishesActionsButton'
import { DishesTable } from './DishesTableV2'
import DishesFooter from './DishesFooter'
import { useDishesSettings } from '../SettingsGear/useDishesSettings'
import { SaleTypesFilter } from './DishesTableV2/filters/SaleTypesFilter'
import { ProcessingLevelFilter } from './DishesTableV2/filters/ProcessingLevelFilter'
import { features } from 'app/features'
import { DishTechCardDownload } from './DishTechCardDownload'

import styles from './Dishes.module.scss'

type Props = {
  writeAccess: boolean
  baseUrl: string
  act?: string
  onRequestRouterHome: () => void
  createTechCardLink: (dishId: DishId) => string
}

const Dishes = ({ writeAccess, baseUrl, act, onRequestRouterHome, createTechCardLink }: Props) => {
  const { rows, ...settings } = useDishesSettings()

  const { setAll } = useCheckboxActions()

  useEffect(() => setAll(false), [rows]) // eslint-disable-line react-hooks/exhaustive-deps

  const [search, setSearch] = useState('')

  const [filterOpened, setFilterOpened] = useState(false)
  const [filterData, setFilterData] = useState<{
    saleTypeId?: Array<string>
    processingLevel?: Array<string>
  } | null>(null)

  const handleApplyFilter = (e: ChangeEvent<HTMLFormElement>) => {
    const formData = new FormData(e.target)
    const saleTypeId = formData.getAll('saleType') as Array<string> | undefined
    const processingLevel = formData.getAll('processingLevel') as Array<string> | undefined
    setFilterData(
      filter((val) => val !== undefined, {
        saleTypeId,
        processingLevel,
      }),
    )
  }

  const handleResetFilter = () => {
    setFilterData(null)
  }

  return (
    <DishesData limit={rows} saleTypeId={filterData?.saleTypeId} search={search}>
      <SaleTypesData>
        <>
          <Sticky>
            {(ref) => (
              <PageTemplateContent
                className={styles.hasSettingsDropdown}
                reference={ref}
                horizontalLayout
              >
                <Header2 skipMargin>{'Список продукции'}</Header2>
                <DishesGear disabledSetup={!writeAccess} />
              </PageTemplateContent>
            )}
          </Sticky>
          <Sticky>
            {(
              ref, // toolbar
            ) => (
              <>
                <PageTemplateContent
                  reference={ref}
                  className={styles.toolbar}
                  borderBottom={!filterOpened}
                  bottomFilterArrowDecoration={filterOpened}
                  bottomFilterArrowDecorationShift={240}
                >
                  <ControlsBar spaceBetween>
                    <ControlsBar left>
                      <DishesAddButton baseUrl={baseUrl} />
                      <PlainButtonFilterControl
                        onClick={() => setFilterOpened((prev) => !prev)}
                        checked={filterData !== null}
                        opened={filterOpened}
                      />
                      <DishesSearch onChangeSearch={setSearch} />
                    </ControlsBar>
                    <ControlsBar right>
                      <ExportButton>{'Экспорт'}</ExportButton>
                      {writeAccess && <DishesActionsButton />}
                    </ControlsBar>
                  </ControlsBar>
                </PageTemplateContent>

                {features.dishes.techCardDownload && <DishTechCardDownload />}

                <PageTemplateContent skip={!filterOpened} skipPadding animate>
                  <PageTemplateFilter
                    onClose={() => setFilterOpened(false)}
                    onApplyFilter={handleApplyFilter}
                    onResetAll={handleResetFilter}
                  >
                    <PageTemplateFilter.Column>
                      <SaleTypesFilter />
                    </PageTemplateFilter.Column>

                    {features.dishes.processingLevel && (
                      <PageTemplateFilter.Column>
                        <ProcessingLevelFilter />
                      </PageTemplateFilter.Column>
                    )}
                  </PageTemplateFilter>
                </PageTemplateContent>
              </>
            )}
          </Sticky>

          <PageTemplateContent skipPadding>
            <DishesTable
              locked={!writeAccess}
              createTechCardLink={createTechCardLink}
              settings={settings}
            />
          </PageTemplateContent>

          <Sticky stickyBottom>
            {(ref, stuck, shadow) => (
              <PageTemplateContent reference={ref} shadowTop={shadow} skipPadding>
                <DishesFooter />
              </PageTemplateContent>
            )}
          </Sticky>

          {/* Добавление блюд */}
          {act === 'add' && <Add closePath={baseUrl} onClose={onRequestRouterHome} />}
        </>
      </SaleTypesData>
    </DishesData>
  )
}

export default Dishes
